import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScrollView, View } from 'react-native';

/** Room left above a card scrolled into view (the status band over the page). */
const TOP_GAP = 96;
/** How long to wait for a folded step to open and lay out its card before scrolling to the step instead. */
const FALLBACK_MS = 400;

/** The hero's latest "Fix N videos": which video, and a press count so a repeat press still reopens. */
export type FocusRequest = { videoId: string; n: number };

/**
 * MarketPryce Social deal page: the hero's "Fix 1 video" scrolls the page to
 * that video's card. Cards (and, as a fallback, the step holding the video
 * under `step:<id>`) register their View by id; the screen hands `scrollRef`
 * to its ScrollView. A folded step opens on `focusRequest`, and the scroll
 * runs once its card mounts; if it never does, the step row is the target.
 */
export function useVideoScroll() {
  const scrollRef = useRef<ScrollView>(null);
  const targets = useRef(new Map<string, View>());
  const pending = useRef<string | null>(null);
  // The scheduled scroll and fallback, cancelled when the page goes away.
  const frame = useRef<number | null>(null);
  const fallback = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      if (fallback.current !== null) clearTimeout(fallback.current);
    },
    [],
  );

  const scrollTo = useCallback((id: string): boolean => {
    const target = targets.current.get(id);
    const scroll = scrollRef.current;
    // RN 0.81 has getInnerViewRef (Fabric measures against the content view); its types lag.
    const content = (scroll as unknown as { getInnerViewRef?: () => View | null } | null)?.getInnerViewRef?.();
    if (!target || !scroll || !content) return false;
    target.measureLayout(content, (_x, y) => scroll.scrollTo({ y: Math.max(0, y - TOP_GAP), animated: true }));
    return true;
  }, []);

  const registerVideo = useCallback(
    (id: string, view: View | null) => {
      if (!view) {
        targets.current.delete(id);
        return;
      }
      targets.current.set(id, view);
      if (pending.current === id) {
        pending.current = null;
        // Once the opened step has laid out.
        frame.current = requestAnimationFrame(() => {
          frame.current = null;
          scrollTo(id);
        });
      }
    },
    [scrollTo],
  );

  const scrollToVideo = useCallback(
    (id: string) => {
      setFocusRequest((prev) => ({ videoId: id, n: (prev?.n ?? 0) + 1 }));
      if (scrollTo(id)) return;
      pending.current = id;
      if (fallback.current !== null) clearTimeout(fallback.current);
      fallback.current = setTimeout(() => {
        fallback.current = null;
        if (pending.current !== id) return;
        pending.current = null;
        scrollTo(`step:${id}`);
      }, FALLBACK_MS);
    },
    [scrollTo],
  );

  return { scrollRef, registerVideo, scrollToVideo, focusRequest };
}
